import React from "react";
import { FaPlus, FaTrash } from "react-icons/fa";

import {
  ArrayCard,
  ArrayCardBody,
  ArrayCardHeader,
  ArrayCardTitle,
  CollectionHeader,
  CollectionGrid,
  CollectionRow,
  CollectionRowSurface,
  CollectionRows,
  CollectionSection,
  DangerIconButton,
  FieldHelper,
  FieldLabel,
  SecondaryButton,
} from "./styles/admin-forms.styles";

type FlexibleCollectionFieldProps<T> = {
  label: string;
  helper?: string;
  items: T[];
  addLabel: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
  renderRow: (item: T, index: number) => React.ReactNode;
  renderItemTitle?: (item: T, index: number) => string;
  disableRemove?: (items: T[], index: number) => boolean;
  cardLayout?: boolean;
  /** Wraps each row in a bordered surface so paired inputs read as one entry. */
  rowSurface?: boolean;
};

export const FlexibleCollectionField = <T,>({
  addLabel,
  cardLayout = false,
  disableRemove,
  helper,
  items,
  label,
  onAdd,
  onRemove,
  renderItemTitle,
  renderRow,
  rowSurface = false,
}: FlexibleCollectionFieldProps<T>) => {
  const removeDisabledResolver = disableRemove || ((rows: T[]) => rows.length === 1);
  const RowWrapper = rowSurface ? CollectionRowSurface : CollectionRow;

  return (
    <CollectionSection>
      <CollectionHeader>
        <div>
          <FieldLabel>{label}</FieldLabel>
          {helper ? <FieldHelper>{helper}</FieldHelper> : null}
        </div>
        <SecondaryButton onClick={onAdd} type="button">
          <FaPlus size={11} />
          {addLabel}
        </SecondaryButton>
      </CollectionHeader>

      <CollectionRows>
        {items.map((item, index) =>
          cardLayout ? (
            <ArrayCard key={`${label}-${index}`}>
              <ArrayCardHeader>
                <ArrayCardTitle>
                  {renderItemTitle ? renderItemTitle(item, index) : `${label} ${index + 1}`}
                </ArrayCardTitle>
                <DangerIconButton
                  disabled={removeDisabledResolver(items, index)}
                  onClick={() => onRemove(index)}
                  type="button"
                >
                  <FaTrash size={11} />
                </DangerIconButton>
              </ArrayCardHeader>
              <ArrayCardBody>{renderRow(item, index)}</ArrayCardBody>
            </ArrayCard>
          ) : (
            <RowWrapper key={`${label}-${index}`}>
              <CollectionGrid>{renderRow(item, index)}</CollectionGrid>
              <DangerIconButton
                disabled={removeDisabledResolver(items, index)}
                onClick={() => onRemove(index)}
                type="button"
              >
                <FaTrash size={11} />
              </DangerIconButton>
            </RowWrapper>
          ),
        )}
      </CollectionRows>
    </CollectionSection>
  );
};
